import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  FaChevronDown,
  FaClock,
  FaChevronLeft,
  // FaChevronRight,
  FaPaw,
} from "react-icons/fa";
import {
  GiSittingDog,
  GiCat,
  GiCow,
  GiGoat,
  GiRabbit,
  GiSquirrel,
  GiTortoise,
  GiHummingbird,
} from "react-icons/gi";
import { useCart } from "../../context/CartContext";
// import { toast } from "react-toastify";
import TimeSlot from "./TimeSlot";
import BoardingCalendar from "./BoardingCalendar";
// import Timeslotsfordaycare from "./Timeslotsfordaycare";
import PlanPricing from "./PlanPricing";
import ImageGalleryModal from "./ImageGalleryModal";
import { IoClose } from "react-icons/io5";
import { isHiddenVendorService } from "../../utils/ServiceTypeMapping";
import config from "../../config";

const mapPetType = (type) => {
  if (!type || typeof type !== "string") return [];

  switch (type.toLowerCase()) {
    case "dog":
      return [{
        icon: <GiSittingDog className="w-6 h-6 text-gray-700" />,
        name: "Dog",
      }];

    case "cat":
      return [{
        icon: <GiCat className="w-6 h-6 text-gray-700" />,
        name: "Cat",
      }];

    case "cow":
      return [{
        icon: <GiCow className="w-6 h-6 text-gray-700" />,
        name: "Cow",
      }];

    case "goat":
      return [{
        icon: <GiGoat className="w-6 h-6 text-gray-700" />,
        name: "Goat",
      }];

    case "rabbit":
      return [{
        icon: <GiRabbit className="w-6 h-6 text-gray-700" />,
        name: "Rabbit",
      }];

    case "squirrel":
      return [{
        icon: <GiSquirrel className="w-6 h-6 text-gray-700" />,
        name: "Squirrel",
      }];

    case "tortoise":
      return [{
        icon: <GiTortoise className="w-6 h-6 text-gray-700" />,
        name: "Tortoise",
      }];

    case "pigeon":
    case "parrot":
      return [{
        icon: <GiHummingbird className="w-6 h-6 text-gray-700" />,
        name: type,
      }];

    default:
      return [];
  }
};


const getPetTypeImages = (petTypeInput) => {
  if (!petTypeInput) return [];

  if (typeof petTypeInput === "string") {
    return mapPetType(petTypeInput);
  }

  if (Array.isArray(petTypeInput)) {
    return petTypeInput.flatMap((pet) =>
      mapPetType(pet?.name)
    );
  }

  if (typeof petTypeInput === "object") {
    return mapPetType(petTypeInput?.name);
  }

  return [];
};

function getServicePrice(service) {
  if (!service) return 0;
  if (service.basic_price && service.basic_price > 0) {
    return service.basic_price;
  }
  if (
    service.pricing?.default?.one_time &&
    service.pricing.default.one_time > 0
  ) {
    return service.pricing.default.one_time;
  }

  if (service.pricing) {
    const durations = Object.values(service.pricing);
    const prices = durations
      .map((d) => d.one_time)
      .filter((price) => typeof price === "number" && price > 0);

    if (prices.length > 0) {
      return Math.min(...prices);
    }
  }

  return 0;
}

const S3_BASE_URL = config.S3_BASE_URL || process.env.REACT_APP_S3_BASE_URL;

const ServiceCard = ({ services = [], vendor = null }) => {
  const [activeTab, setActiveTab] = useState("");
  const [showDetail, setShowDetail] = useState({});
  const [page, setPage] = useState({});
  const { addToCart, updateQuantity, cartItems, user } = useCart();
  const [pendingService, setPendingService] = useState(null);
  const [showBlur, setShowBlur] = useState(false);

  const getCartItemQuantityForService = (service, index) => {
    const uniqueServiceId = `${service.name}_${index}_${activeTab}`;
    const serviceItems = cartItems.filter(
      (item) => item.id === uniqueServiceId && item.service_type === activeTab
    );
    return serviceItems.reduce(
      (total, item) => total + (item.quantity || 1),
      0
    );
  };
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [isMobile, setIsMobile] = useState(false);
  const MOBILE_LIMIT = 10;
  const DESKTOP_LIMIT = 20;

  const [showPlanPricingModalForService, setShowPlanPricingModalForService] =
    useState(null); // Will store { service, index }

  // ⭐️ EFFECT TO DETECT MOBILE SIZE AND LARGE SCREEN
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 1024);

  const [isSidebarActive, setIsSidebarActive] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  // Filter out hidden service groups and services with empty names.
   const filteredServiceGroups = useMemo(() => {
    return (services || [])
      .map((group) => {
        const validServices = (group.services || []).filter(
          (service) => service?.name && service.name.trim() !== ""
        );

        if (
          !group.service_type_name ||
          isHiddenVendorService(group.service_type_name) ||
          validServices.length === 0
        ) {
          return null;
        }

        return { ...group, services: validServices };
      })
      .filter(Boolean);
  }, [services]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
      setIsLargeScreen(window.innerWidth >= 1024);
    };

    handleResize(); // Initial check
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    // Only lock scroll if:
    // 1. PlanPricing modal is open (always modal)
    // 2. OR PendingService is open AND we are on a smaller screen (not large)
    const shouldLock =
      showPlanPricingModalForService || (pendingService && !isLargeScreen);

    if (shouldLock) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [pendingService, showPlanPricingModalForService, isLargeScreen]);

  // ⭐️ HANDLER TO TOGGLE DESCRIPTION EXPANSION
  const toggleDescription = (index) => {
    setExpandedDescriptions((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };
 
  const handleAddToCartAndReset = (finalData) => {
  // 1. Add the item to the cart context
  addToCart(finalData);

  // 2. Common cleanup
  setShowBlur(false);
  setIsSidebarActive(false);
  setResetKey((prev) => prev + 1);

  // 3. Different reset for Desktop vs Mobile
  if (isLargeScreen) {
    // DESKTOP / TABLET: Restore locked "first service" view (no "No service selected")
    if (filteredServiceGroups.length > 0) {
      const firstGroup = filteredServiceGroups[0];
      const firstService = firstGroup?.services?.[0];

      if (firstService) {
        const pendingData = formatPendingServiceData(
          firstService,
          firstGroup.service_type_name,
          vendor
        );

        const needsPlanSelection = ["dog walks", "day care"].includes(
          firstGroup.service_category_name?.toLowerCase().trim()
        );

        setPendingService({
          ...pendingData,
          needsPlanSelection,
          sourceService: firstService,
          sourceIndex: 0,
        });
      }
    }
  } else {
    // MOBILE: Close the modal
    setPendingService(null);
  }
};
   const isFirstMount = useRef(true);

  useEffect(() => {
      if (!isFirstMount.current) return; // Skip effect on updates
      isFirstMount.current = false;      // Mark that first mount is done

    if (filteredServiceGroups.length > 0) {
      const initialTab = filteredServiceGroups[0].service_type_name;
      setActiveTab(initialTab);

      // ONLY auto-select first service if on Desktop
      if (isLargeScreen) {
        const firstServiceGroup = filteredServiceGroups[0];
        const firstService = firstServiceGroup?.services?.[0];

        if (firstService) {
          const pendingData = formatPendingServiceData(
            firstService,
            initialTab,
            vendor
          );

          const needsPlanSelection = ["dog walks", "day care"].includes(
            firstServiceGroup.service_category_name?.toLowerCase().trim()
          );

          setPendingService({
            ...pendingData,
            needsPlanSelection,
            sourceService: firstService,
            sourceIndex: 0,
          });

          setIsSidebarActive(false);
        }
      }
    }
  }, [filteredServiceGroups, isLargeScreen]);

  useEffect(() => {
  if (showPlanPricingModalForService) {
    console.log("🔍 SOURCE DATA CHECK:", {
      serviceName: showPlanPricingModalForService.service?.name,
      serviceVendorId: showPlanPricingModalForService.service?.vendor_id, // THIS IS THE TRUTH
      propVendorId: vendor?._id // THIS IS THE POTENTIAL SPOOF
    });
  }
}, [showPlanPricingModalForService]);

useEffect(() => {
  if (isLargeScreen && !pendingService && filteredServiceGroups.length > 0) {
    const firstGroup = filteredServiceGroups[0];
    const firstService = firstGroup?.services?.[0];
    if (firstService) {
      const pendingData = formatPendingServiceData(
        firstService,
        firstGroup.service_type_name,
        vendor
      );
      setPendingService({
        ...pendingData,
        needsPlanSelection: ["dog walks", "day care"].includes(
          firstGroup.service_category_name?.toLowerCase().trim()
        ),
        sourceService: firstService,
        sourceIndex: 0,
      });
    }
  }
}, [filteredServiceGroups, isLargeScreen, pendingService, vendor]);

  const toggleDetail = (index) => {
    setShowDetail((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleNext = (serviceKey, imagesLength) => {
    setPage((prev) => ({
      ...prev,
      [serviceKey]: (prev[serviceKey] || 0) + 1,
    }));
  };

  const handlePrev = (serviceKey) => {
    setPage((prev) => ({
      ...prev,
      [serviceKey]: Math.max((prev[serviceKey] || 0) - 1, 0),
    }));
  };

  // ⭐️ GALLERY STATE
  const [galleryImages, setGalleryImages] = useState([]);
  const [currentGalleryIndex, setCurrentGalleryIndex] = useState(0);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);

  const openGallery = (images, index) => {
    setGalleryImages(
      images && images.length > 0 ? images : ["/img/building.png"]
    );
    setCurrentGalleryIndex(index);
    setIsGalleryOpen(true);
  };

  const formatPendingServiceData = (service, serviceType, currentVendor) => {
    // Extract size-based pricing
    const priceBySize = Array.isArray(service?.price_by_size)
      ? service.price_by_size.map((item) => ({
          size: item.size,
          price: item.price,
        }))
      : [];
     const basicPrice = service.basic_price || service.price || 0;
     
    return {
      service_id: service?._id,
      name: service.name,
      basic_price: basicPrice, 
      //price: getServicePrice(service),
      description: service.description,
      duration: service.duration?.flat?.minutes,
      pet_type: service.pet_type,
      service_type: serviceType,
      vendor_name: currentVendor?.profile?.brand,
      vendor_id: currentVendor?.vendor_id,
       sourceService: service,
      
      room_type_surcharges: service?.room_type_surcharges,
      // ✅ Price by pet size
      //price_by_size:
       // service?.size_surcharges?.enabled === "yes"
          //? service.size_surcharges.options.map((opt) => ({
           //   size: opt.size,
           //   price: opt.price,
          //  }))
          //: [],

      // ✅ Price by room type
      //price_by_room_type:
      //  service?.room_type_surcharges?.enabled === "yes"
        //  ? service.room_type_surcharges.options.map((opt) => ({
           //   room_type: opt.room_type,
            //  price: opt.price,
           // }))
         // : [],
    };
  };

  const openPendingService = (service, index) => {
    const pendingData = formatPendingServiceData(service, activeTab, vendor);
    
    setPendingService(pendingData);
    setIsSidebarActive(true);
    setShowBlur(true);
  };

  // ⭐️ HANDLE TAB CLICK WITH AUTO-SELECT FOR DESKTOP
  const handleTabClick = (newTab) => {
    setActiveTab(newTab);

    const newGroup = filteredServiceGroups.find(
      (s) => s.service_type_name === newTab
    );
    if (isLargeScreen && newGroup?.services?.length > 0) {
      const firstService = newGroup.services[0];

      const needsPlanSelection = ["dog walks", "day care"].includes(
        newGroup.service_category_name?.toLowerCase().trim()
      );

      const pendingData = formatPendingServiceData(
        firstService,
        newTab,
        vendor
      );
      setPendingService({
        ...pendingData,
        needsPlanSelection,
        sourceService: firstService,
        sourceIndex: 0,
      });
       
      setIsSidebarActive(false);

    } else {
      setPendingService(null);
    }
  };

  const activeServiceGroup = filteredServiceGroups.find(
    (s) => s.service_type_name === activeTab
  );
  
  const isBoarding = activeServiceGroup?.service_category_name?.toLowerCase().includes("boarding");
  const isGrooming = activeServiceGroup?.service_category_name?.toLowerCase().includes("grooming");

  

  // Debug: Track pendingService changes
  useEffect(() => {
  
  }, [pendingService]);

  const getTruncatedText = (text, isExpanded, serviceIndex) => {
    const limit = isMobile ? MOBILE_LIMIT : DESKTOP_LIMIT;
    const words = text.split(/\s+/).filter(Boolean); // Filter(Boolean) handles multiple spaces
    const needsTruncation = words.length > limit;

    if (isExpanded || !needsTruncation) {
      return {
        displayedText: text,
        needsTruncation: needsTruncation,
      };
    }

    return {
      displayedText: words.slice(0, limit).join(" ") + "...",
      needsTruncation: needsTruncation,
    };
  };

  const truncateString = (str, maxLength = 20) => {
    if (!str) return '';
    return str.length > maxLength ? `${str.substring(0, maxLength)}...` : str;
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      {/* Left Section: Services List */}

      <div
        className={`w-full lg:w-3/5 transition-all duration-300 ${
          isLargeScreen && showBlur
            ? "blur-sm opacity-50 pointer-events-none"
            : ""
        }`}
      >
        <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-orange-100 rounded-full">
              <FaPaw className="text-[#FD5901] text-xl" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#000048]">
              Our <span className="text-[#FD5901]">Services</span>
            </h2>
          </div>

          {/* Service Type Tabs - Horizontal Scroll on Mobile */}
         
          <div className="flex overflow-x-auto pb-2 gap-3 mb-6 no-scrollbar mask-gradient-right">
            {filteredServiceGroups.map((group, index) => (
              <button
                key={index}
                onClick={() => handleTabClick(group.service_type_name)}
                className={`flex-shrink-0 py-2.5 px-6 rounded-full text-sm font-bold tracking-wide transition-all duration-300 border ${
                  activeTab === group.service_type_name
                    ? "bg-[#FD5901] text-white border-[#FD5901] shadow-lg shadow-orange-200 transform scale-105"
                    : "bg-white text-gray-600 border-gray-200 hover:border-orange-300 hover:text-[#FD5901] hover:bg-orange-50"
                }`}
              >
                {group.service_type_name}
              </button>
            ))}
          </div>

          {/* Services List */}

          <div className="space-y-4">
            {activeServiceGroup?.services
            ?.filter((service) => service.pet_type && service.pet_type.length > 0)// Hides services with no pets
            .map((service, index) => {

                 

              const serviceKey = `${activeTab}_${index}`;
              const inCart = cartItems.some(
                (item) =>
                  item.id === `${service.name}_${index}_${activeTab}` &&
                  item.service_type === activeTab
              );
              const { displayedText, needsTruncation } = getTruncatedText(
                service.description || "No description available.",
                expandedDescriptions[serviceKey],
                serviceKey
              );

              return (
                <div
                  key={index}
                  className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-lg transition-shadow duration-300"
                >
                  <div className="p-4">
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                      {/* Left side: Icon and Pet Types */}

                      <div className="flex-shrink-0 flex flex-col items-center sm:items-start sm:border-r sm:pr-4">
                        <img
                          src="/img/dogPaw.png"
                          alt="service icon"
                          className="h-8 w-8"
                        />
                      </div>

                      {/* Middle: Service Details */}

                      <div className="flex-grow">
                        <h3 className="text-lg sm:text-xl font-bold text-gray-800">
                          {truncateString(service.name || "Untitled Service")}
                        </h3>

                        {/* {!["boarding", "day care"].includes(
                          activeServiceGroup?.service_category_name
                            ?.toLowerCase()
                            .trim()
                        ) &&  
                             service?.duration?.flat?.minutes > 0 && ( */}
                             {!isBoarding &&
  activeServiceGroup?.service_category_name?.toLowerCase().trim() !== "day care" &&  
     service?.duration?.flat?.minutes > 0 && (
                          <p className="text-sm text-gray-600 flex items-center gap-2 mt-1">
                            <FaClock />

                            <span>
                              {service.duration?.flat?.minutes
                                ? `${service.duration.flat.minutes} minutes`
                                : " "}
                            </span>
                          </p>
                        )}

                         <div className="flex items-start gap-2 mt-2 min-w-0">
                          <p className="text-sm text-gray-600 shrink-0">For:</p>
                          {getPetTypeImages(service.pet_type).length > 0 && (
                             <div className="flex flex-wrap items-center gap-x-3 gap-y-2 min-w-0 flex-1">
                              {getPetTypeImages(service.pet_type).map((pet, i) => (
                                <div key={i} className="flex items-center gap-2">
                                  {pet.icon}
                                  <p className="text-xs text-gray-600 font-medium">
                                    {pet.name}
                                  </p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {service.description && (
                         <>
                          <p className="text-sm text-gray-600 mt-2">
                          {getTruncatedText(service.description, expandedDescriptions[serviceKey], serviceKey).displayedText}
                          </p>

                          {getTruncatedText(service.description, expandedDescriptions[serviceKey], serviceKey).needsTruncation && (
                          <button
                          onClick={() => toggleDescription(serviceKey)}
                         className="text-orange-500 hover:text-orange-700 text-sm font-semibold mt-1 transition-colors"
                            >
                            {expandedDescriptions[serviceKey] ? "Read Less" : "Read More"}
                         </button>
                       )}
                      </>
                      )}
                     </div>
                      {/* Right side: Price & Actions */}

                      <div className="flex-shrink-0 flex flex-col items-center justify-center sm:pl-4 sm:border-l border-gray-200/80">
                        {/* Main Container: Row on mobile/tablet, Column on large */}
                        <div className="w-full flex flex-row lg:flex-col items-center justify-between lg:justify-center gap-4 lg:gap-6">
                          {/* Price Section - Left on mobile/tablet, Center on large */}
                          <div className="flex flex-row items-center gap-2 lg:flex-col lg:items-center lg:gap-1">
                            <p className="text-sm text-gray-500 whitespace-nowrap">
                              From
                            </p>
                            <p className="text-xl font-bold text-gray-800">
                              ₹{getServicePrice(service)}
                            </p>
                          </div>

                          {/* Button / Quantity Section - Right on mobile/tablet, Center below on large */}
                          <div className="flex justify-end lg:justify-center">
                            
                            {["dog walks", "day care"].includes(
                              activeServiceGroup?.service_category_name
                                ?.toLowerCase()
                                .trim()
                            ) ? (
                              // Complex service flow
                              
                              inCart ? (
                                <div className="flex items-center gap-2.5">
                                  <button
                                    className="w-8 h-8 rounded-full border-2 border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white transition-all font-bold text-base flex items-center justify-center"
                                    onClick={() =>
                                      setShowPlanPricingModalForService({
                                        service,
                                        index,
                                      })
                                    }
                                  >
                                    -
                                  </button>

                                  <span className="font-bold text-lg min-w-[28px] text-center">
                                    {getCartItemQuantityForService(
                                      service,
                                      index
                                    )}
                                  </span>

                                  <button
                                    className="w-8 h-8 rounded-full border-2 border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white transition-all font-bold text-base flex items-center justify-center"
                                    onClick={() =>
                                      setShowPlanPricingModalForService({
                                        service,
                                        index,
                                      })
                                    }
                                  >
                                    +
                                  </button>
                                </div>
                              ) : (
                                <button
                                  className="bg-orange-500 text-white font-bold py-2 px-5 rounded-lg hover:bg-orange-600 transition shadow-md text-sm whitespace-nowrap"
                                  onClick={() => {
                                    if (isLargeScreen) {
                                      const pendingData =
                                        formatPendingServiceData(
                                          service,
                                          activeTab,
                                          vendor
                                        );

                                      setPendingService({
                                        ...pendingData,
                                        needsPlanSelection: true,
                                        sourceService: service,
                                        sourceIndex: index,
                                      });
                                      setIsSidebarActive(true);
                                      setShowBlur(true);
                                    } else {
                                      setShowPlanPricingModalForService({
                                        service,
                                        index,
                                      });
                                    }
                                  }}
                                >
                                  Add
                                </button>
                              )
                              
                            ) : // Simple service flow
                            inCart ? (
                              <div className="flex items-center gap-2.5">
                                <button
                                  className="w-8 h-8 rounded-full border-2 border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white transition-all font-bold text-base flex items-center justify-center"
                                  onClick={() =>
                                    openPendingService(service, index)
                                  }
                                >
                                  -
                                </button>

                                <span className="font-bold text-lg min-w-[28px] text-center">
                                  {getCartItemQuantityForService(
                                    service,
                                    index
                                  )}
                                </span>

                                <button
                                  className="w-8 h-8 rounded-full border-2 border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white transition-all font-bold text-base flex items-center justify-center"
                                  onClick={() =>
                                    openPendingService(service, index)
                                  }
                                >
                                  +
                                </button>
                              </div>
                            ) : (
                              <button
                                className="bg-orange-500 text-white font-bold py-2 px-5 rounded-lg hover:bg-orange-600 transition shadow-md text-sm whitespace-nowrap"
                                onClick={() =>
                                  openPendingService(service, index)
                                }
                              >
                                Add
                              </button>
                            )}
                          </div>
                        </div>

                        {/* "In Cart" text - below on all screens */}
                        {inCart && (
                          <p className="text-xs text-green-600 mt-4 font-medium">
                            In Cart
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* View Detail Toggle */}
                  {(service.service_inclusions?.some(inc => inc.title?.trim()) || 
                  (service.images && service.images.length > 0)) && (
                    <div
                      className="bg-gray-50 px-4 py-2 cursor-pointer flex justify-between items-center"
                      onClick={() => toggleDetail(serviceKey)}
                    >
                      <p className="text-sm font-semibold text-gray-700">
                        View Details
                      </p>

                      <FaChevronDown
                        className={`text-gray-500 transition-transform ${
                          showDetail[serviceKey] ? "rotate-180" : ""
                        }`}
                      />
                    </div>
                  )}

                  {/* Detail Section */}

                  {showDetail[serviceKey] && (
                    <div className="p-4 border-t border-gray-200">
                      {service.service_inclusions?.some(inc => inc.title?.trim()) && (
                          <>
                            <h4 className="font-semibold text-gray-800 mb-2">
                              What’s included?
                            </h4>
                            <p className="text-sm text-gray-600 mb-4">
                              {service.service_inclusions
                                .map((inc) => inc.title)
                                .join(", ")}
                            </p>
                          </>
                        )}

                      {service.images && service.images.length > 0 && (
                        <>
                          <h4 className="font-semibold text-gray-800 mb-2">
                            Gallery
                          </h4>
                          <div className="flex items-center gap-2">
                            <button
                              className="p-1 rounded-full bg-gray-100 hover:bg-gray-200"
                              onClick={() => handlePrev(serviceKey)}
                              disabled={(page[serviceKey] || 0) === 0}
                            >
                              <FaChevronLeft
                                className={`${
                                  (page[serviceKey] || 0) === 0
                                    ? "opacity-50"
                                    : ""
                                }`}
                              />
                            </button>

                            <div className="flex-grow grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                              {(
                                service.images?.slice(
                                  (page[serviceKey] || 0) * 5,
                                  (page[serviceKey] || 0) * 5 + 5
                                ) || []
                              ).map((img, i) => (
                                <img
                                  key={i}
                                  src={
                                    img.includes("http")
                                      ? img
                                      : `${S3_BASE_URL}/${img}`
                                  }
                                  alt={`gallery item ${i}`}
                                  className="w-full h-20 rounded-lg object-cover cursor-pointer hover:opacity-80 transition"
                                  onClick={() =>
                                    openGallery(
                                      service.images,
                                      (page[serviceKey] || 0) * 5 + i
                                    )
                                  }
                                />
                              ))}
                            </div>

                            <button
                              className="p-1 rounded-full bg-gray-100 hover:bg-gray-200"
                              onClick={() =>
                                handleNext(
                                  serviceKey,
                                  service.images?.length || 1
                                )
                              }
                              disabled={
                                ((page[serviceKey] || 0) + 1) * 5 >=
                                (service.images?.length || 1)
                              }
                            >
                              <FaChevronLeft
                                className={`${
                                  (page[serviceKey] || 0) === 0
                                    ? "opacity-50"
                                    : ""
                                }`}
                              />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {/* Right Section - Desktop View for TimeSlot / Calendar */}
      <div className="w-full lg:w-2/5 relative">
        {isLargeScreen && showBlur  ? (
          <>
            {/* Backdrop overlay - clickable to close */}
           {showBlur && (
        <div 
          // className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[-1]" 
          className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50" 
         // onClick={() => setShowBlur(false)} 
         onClick={() => {
                setShowBlur(false);
                setIsSidebarActive(false);   // ⭐️ THIS WAS MISSING
              }}
              />
            )}
            {/* Right Panel - Above backdrop */}
            <div className="sticky top-24 border border-gray-200 rounded-xl shadow-lg bg-white p-4 z-[100] relative">
              {/* Show PlanPricing first if needed, then appropriate component */}
              {pendingService?.needsPlanSelection ? (
                <PlanPricing
                  pricing={pendingService.sourceService?.pricing}
                  service={pendingService.sourceService}
                  serviceIndex={pendingService.sourceIndex}
                  activeTab={activeTab}
                  vendor={vendor}
                  onSelection={(selectedPlan) => {
                    // Update pendingService with selected plan and remove needsPlanSelection flag
                    
                    setPendingService({
                      ...pendingService,

                      ...selectedPlan,
                       vendor_id: selectedPlan.vendor_id,
                      needsPlanSelection: false,
                    });
                  }}
                />
              ) : activeServiceGroup?.service_category_name?.toLowerCase() ===
                "grooming" ? (
                <TimeSlot
                  pendingService={pendingService}
                  setPendingService={setPendingService}
                  addToCart={handleAddToCartAndReset}
                />
              ) : isBoarding ? (
                <BoardingCalendar
                key={`desktop-cal-${resetKey}`}
                  pendingService={pendingService}
                  setPendingService={setPendingService}
                  addToCart={handleAddToCartAndReset}
                />
              ) : (
                <TimeSlot
                  pendingService={pendingService}
                  setPendingService={setPendingService}
                  addToCart={handleAddToCartAndReset}
                />
              )}
            </div>
          </>
        ) : isLargeScreen && pendingService ? (
          <>
            {/* Right Panel without backdrop - for default display */}
            <div className="sticky top-24 border border-gray-200 rounded-xl shadow-lg bg-white p-4  relative">
              <div className={`transition-all duration-500 ${!isSidebarActive ? "opacity-30 pointer-events-none grayscale-[50%]" : "opacity-100"}`}>
              {/* Show PlanPricing first if needed, then appropriate component */}
              {pendingService.needsPlanSelection ? (
                <PlanPricing
                  pricing={pendingService.sourceService?.pricing}
                  service={pendingService.sourceService}
                  serviceIndex={pendingService.sourceIndex}
                  activeTab={activeTab}
                  vendor={vendor}
                  onSelection={(selectedPlan) => {
                    // Update pendingService with selected plan and remove needsPlanSelection flag
                    setPendingService({
                      ...pendingService,
                      ...selectedPlan,
                      vendor_id: selectedPlan.vendor_id,
                      needsPlanSelection: false,
                    });
                  }}
                />
              ) : activeServiceGroup?.service_category_name?.toLowerCase() ===
                "grooming" ? (
                <TimeSlot
                  pendingService={pendingService}
                  setPendingService={setPendingService}
                  addToCart={handleAddToCartAndReset}
                />
              ) : activeServiceGroup?.service_category_name?.toLowerCase() ===
                "boarding" ? (
                <BoardingCalendar
                  key={`desktop-cal-${resetKey}`}
                  pendingService={pendingService}
                  setPendingService={setPendingService}
                  addToCart={handleAddToCartAndReset}
                />
              ) : (
                <TimeSlot
                  pendingService={pendingService}
                  setPendingService={setPendingService}
                  addToCart={handleAddToCartAndReset}
                />
              )}
              </div> {/* ⭐️ CLOSE TRANSITION DIV HERE */}

           {/* ⭐️ OPTIONAL: Visual Label when disabled */}
             {!isSidebarActive && (
             <div className="absolute inset-0 flex items-center justify-center z-10">
             <span className="bg-[#000048] text-white px-4 py-2 rounded-full text-xs font-bold">
                Click "Add" to select
             </span>
            </div>
             )}
            </div>
          </>
        ) : (
          <div className="p-3 text-center text-gray-500">
            {!pendingService && <p>No service selected</p>}
            {/* If pendingService is true but NOT isLargeScreen, it's shown in modal, so this is blank or hidden */}
          </div>
        )}
      </div>
      {showPlanPricingModalForService && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center p-4 sm:p-6 ">
          <div
            className="absolute inset-0 z-40 bg-black bg-opacity-50 backdrop-blur-sm"
            onClick={() => setShowPlanPricingModalForService(null)}
          />

          <div
            className={`transition-all duration-300 z-50 relative 
                                bg-white rounded-lg shadow-2xl w-full max-w-lg sm:max-w-md 
                                max-h-[95vh] overflow-y-auto transform scale-100 opacity-100`}
          >
            <button
              className="absolute top-3 right-3 p-1 text-4xl text-[#000048] hover:text-[#FD5901] z-10"
              onClick={() => setShowPlanPricingModalForService(null)}
              aria-label="Close Plan Pricing"
            >
              <IoClose />
            </button>

            <div className="p-6">
              <h2 className="text-2xl font-bold text-[#000048] mb-4">
                Select Plan & Duration
              </h2>

              <PlanPricing
                pricing={showPlanPricingModalForService.service.pricing}
                service={showPlanPricingModalForService.service}
                serviceIndex={showPlanPricingModalForService.index}
                activeTab={activeTab}
                vendor={vendor}
                // This is the handler for when 'Add to Cart' is clicked inside PlanPricing
                onSelection={(pending) => {
                  // 1. Close the Plan Pricing modal
                  setShowPlanPricingModalForService(null);
                  // 2. Open the Time Slot modal (Modal 2)
                  
                  setPendingService({
                    ...pending,
                    vendor_id:  vendor?.vendor_id ||vendor?._id ,
                    service_id: showPlanPricingModalForService.service?._id,
                  });
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* ---------------------------------------------------- */}

      {/* GLOBAL MODAL/POP-UP CONTAINER (FIX: Centered and Fixed) */}

      {/* ---------------------------------------------------- */}

      {pendingService && (
        // Modal Backdrop and Wrapper

        <div className="fixed inset-0 z-40 flex flex-col items-center justify-center p-4 sm:p-6 lg:hidden">
          <div
            className="absolute inset-0 z-40"
            style={{
              background: "rgba(0, 0, 0, 0.5)",

              backdropFilter: "blur(8px)",

              WebkitBackdropFilter: "blur(8px)",
            }}
            onClick={() => {
              setPendingService(null);
            }}
          />

          <div
            className={`transition-all duration-300 z-40 relative 

                          bg-white rounded-lg shadow-2xl w-full max-w-md sm:max-w-sm md:max-w-md 

                          max-h-[92vh] h-auto sm:max-h-[90vh] overflow-y-auto transform scale-100 opacity-100`}
          >
            <button
              className="absolute top-1 right-3 p-0 text-5xl text-[#000048] hover:text-[#FD5901]"
              onClick={() => setPendingService(null)}
              aria-label="Close Modal"
            >
              <IoClose />
            </button>

            {/* Conditional Modal Component Rendering */}

            {pendingService &&
            activeServiceGroup?.service_category_name?.toLowerCase() ===
              "grooming" ? (
              <TimeSlot
                pendingService={pendingService}
                setPendingService={setPendingService}
                addToCart={handleAddToCartAndReset}
              />
            ) : pendingService &&
              isBoarding ? (
              <BoardingCalendar
              key={`mobile-cal-${resetKey}`}
                pendingService={pendingService}
                setPendingService={setPendingService}
                addToCart={handleAddToCartAndReset}
              />
            ) : pendingService &&
              ["day care", "dog walks"].includes(
                activeServiceGroup?.service_category_name?.toLowerCase()
              ) ? (
              <TimeSlot
                pendingService={pendingService}
                setPendingService={setPendingService}
                addToCart={handleAddToCartAndReset}
              />
            ) : pendingService ? (
              <TimeSlot
                pendingService={pendingService}
                setPendingService={setPendingService}
                addToCart={handleAddToCartAndReset}
              />
            ) : null}
          </div>
        </div>
      )}

      <ImageGalleryModal
        images={galleryImages}
        initialIndex={currentGalleryIndex}
        isOpen={isGalleryOpen}
        onClose={() => setIsGalleryOpen(false)}
      />
    </div>
  );
};
export default ServiceCard;
